import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Card } from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import { useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  useGetArea,
  useGetSubArea,
} from "@features/mx/readCycleManagement/hooks";
import { usePremiseModal } from "../../hooks";

interface PremiseTabProps {
  selectedPremises: string[];
  setSelectedPremises: React.Dispatch<React.SetStateAction<string[]>>;
  initialPremises: any;
}

export const PremiseTab = ({
  selectedPremises,
  setSelectedPremises,
  initialPremises,
}: PremiseTabProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = searchParams.get("premise_search")
    ? decodeURIComponent(searchParams.get("premise_search") || "")
    : "";
  const currentArea = searchParams.get("premise_area") || "all";
  const currentSubArea = searchParams.get("premise_subArea") || "all";

  const [pendingFilters, setPendingFilters] = useState({
    area: currentArea,
    subArea: currentSubArea,
  });

  const { data: areaData } = useGetArea();
  const { data: subAreaData } = useGetSubArea(
    currentArea !== "all" ? [currentArea] : []
  );

  // Use the usePremiseModal hook with the correct parameters
  const { data: premiseData, isLoading: isLoadingPremises } = usePremiseModal(
    currentPage,
    currentArea,
    currentSubArea,
    currentSearch,
    initialPremises
  );

  // Transform data for display
  const premiseDetail = useMemo(
    () =>
      premiseData?.result?.map((item: any) => ({
        id: item.hierarchyCodes?.premise,
        name: item.name || "NA",
        area: item.hierarchyNames?.area || "NA",
        subArea: item.hierarchyNames?.subArea || "NA",
        meters: item.installedMeterCount ?? "NA",
        consumers: item.billedUnitCount ?? "NA",
      })) || [],
    [premiseData]
  );

  const areaOptions = useMemo(
    () =>
      areaData?.map((area) => ({
        value: String(area.code),
        label: area.name,
      })) || [],
    [areaData]
  );

  const subAreaOptions = useMemo(
    () =>
      subAreaData?.map((subArea) => ({
        value: String(subArea.code),
        label: subArea.name,
      })) || [],
    [subAreaData]
  );

  const allSelected = useMemo(
    () =>
      selectedPremises.length === premiseDetail.length &&
      premiseDetail.length > 0,
    [selectedPremises, premiseDetail]
  );

  // Optimize handlers with useCallback
  const handleSelect = useCallback(
    (id: string, checked: boolean) => {
      setSelectedPremises((prev) =>
        checked ? [...prev, id] : prev.filter((x) => x !== id)
      );
    },
    [setSelectedPremises]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedPremises(checked ? premiseDetail.map((r) => r.id) : []);
    },
    [premiseDetail, setSelectedPremises]
  );

  const handlePendingFilterChange = useCallback(
    (key: keyof typeof pendingFilters, value: string) => {
      setPendingFilters((prev) => {
        if (key === "area") {
          return {
            ...prev,
            [key]: value,
            subArea: "all", // Reset subArea when area changes
          };
        }
        return {
          ...prev,
          [key]: value,
        };
      });
    },
    []
  );

  const handleApplyFilters = useCallback(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("premise_area", pendingFilters.area);
      newParams.set("premise_subArea", pendingFilters.subArea);
      newParams.set("page", "1");
      return newParams;
    });
  }, [pendingFilters, setSearchParams]);

  const handleResetFilters = useCallback(() => {
    setPendingFilters({
      area: "all",
      subArea: "all",
    });

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("premise_area");
      newParams.delete("premise_subArea");
      newParams.delete("premise_search");
      newParams.set("page", "1");
      return newParams;
    });
  }, [setSearchParams]);

  const handleSearch = useCallback(
    (searchTerm: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (searchTerm.trim()) {
          newParams.set("premise_search", searchTerm.trim());
        } else {
          newParams.delete("premise_search");
        }

        newParams.set("page", "1");
        return newParams;
      });
    },
    [setSearchParams]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("page", String(page));
        return newParams;
      });
    },
    [setSearchParams]
  );

  // Memoize the filter panel to prevent unnecessary re-renders
  const filterPanel = useMemo(
    () => (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Area</label>
            <Select
              value={pendingFilters.area}
              onValueChange={(value) => handlePendingFilterChange("area", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                {areaOptions.map((area) => (
                  <SelectItem key={area.value} value={area.value}>
                    {area.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                pendingFilters.area === "all"
                  ? "text-gray-400"
                  : "text-gray-700"
              }`}
            >
              Sub Area
            </label>
            <Select
              value={pendingFilters.subArea}
              onValueChange={(value) =>
                handlePendingFilterChange("subArea", value)
              }
              disabled={pendingFilters.area === "all"}
            >
              <SelectTrigger
                className={`w-full ${
                  pendingFilters.area === "all"
                    ? "bg-gray-100 border-gray-300 cursor-not-allowed text-gray-400 opacity-60"
                    : "bg-white"
                }`}
              >
                <SelectValue placeholder="Select sub area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sub Areas</SelectItem>
                {subAreaOptions.map((subArea) => (
                  <SelectItem key={subArea.value} value={subArea.value}>
                    {subArea.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="default" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
          <Button variant="ghost" onClick={handleResetFilters}>
            Clear
          </Button>
        </div>
      </div>
    ),
    [
      pendingFilters,
      areaOptions,
      subAreaOptions,
      handlePendingFilterChange,
      handleApplyFilters,
      handleResetFilters,
    ]
  );

  // Memoize table columns to prevent unnecessary re-renders
  const columns = useMemo<TableColumn<any>[]>(
    () => [
      {
        key: "select",
        header: (
          <div className="flex justify-center items-center">
            <Checkbox
              checked={allSelected}
              onCheckedChange={(checked) => handleSelectAll(!!checked)}
            />
          </div>
        ),
        type: "custom",
        sortable: false,
        width: "w-5",
        render: (_, row) => (
          <div className="flex justify-center items-center">
            <Checkbox
              checked={selectedPremises.includes(row.id)}
              onCheckedChange={(checked) => handleSelect(row.id, !!checked)}
            />
          </div>
        ),
      },
      {
        key: "name",
        header: "Premise",
        headerTooltip:
          "Building or location name. Each premise can contain multiple meters and consumers.",
      },
      {
        key: "area",
        header: "Area",
        headerTooltip:
          "Geographic zone for route planning. Use to group nearby premises and optimize travel time.",
      },
      {
        key: "subArea",
        header: "Subarea",
        headerTooltip:
          "Subdivision within an area for granular route organization and workload distribution.",
      },
      {
        key: "meters",
        header: "Meters",
        headerTooltip:
          "Number of meters at this premise. Higher counts indicate larger workload for field readers.",
      },
    ],
    [allSelected, selectedPremises, handleSelectAll, handleSelect]
  );

  // Calculate pagination
  const totalItems = premiseData?.count || 0;
  const totalPages = Math.ceil(totalItems / 10) || 1;
  const startItem = totalItems > 0 ? (currentPage - 1) * 10 + 1 : 0;
  const endItem = totalItems > 0 ? Math.min(currentPage * 10, totalItems) : 0;

  return (
    <Card className="shadow-sm">
      {selectedPremises.length > 0 && (
        <div className="flex items-center justify-between mb-3 bg-blue-50 border border-blue-100 text-blue-700 text-sm px-3 py-2 rounded-md shadow-sm">
          <span className="font-medium">
            {selectedPremises.length} premise
            {selectedPremises.length > 1 ? "s" : ""} selected
          </span>
        </div>
      )}
      <GenericTable
        data={premiseDetail}
        columns={columns}
        rowKey="id"
        loading={isLoadingPremises}
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage: 10,
          startItem,
          endItem,
        }}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search premises..."
        emptyMessage="No premises found"
        filterPanel={filterPanel}
        searchValue={currentSearch}
      />
    </Card>
  );
};
